import React, { useEffect, useMemo } from "react";
import {
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  MessageBarIntent,
  Button,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useAppStore } from "../../../common";
import { useSiteMessageBarStyles } from "./SiteMessageBarStyles";
import { ISiteMessage, MessageSeverity } from "../../../common/store/states/siteMessage";

const mapSeverityToIntent = (severity: MessageSeverity): MessageBarIntent => {
  switch (severity) {
    case "warning":
      return "warning";
    case "error":
      return "error";
    case "success":
      return "success";
    case "info":
    default:
      return "info";
  }
};

const severityOrder: Record<MessageSeverity, number> = {
  error: 0,
  warning: 1,
  success: 2,
  info: 3,
};

const LINE_BREAK_MARKER = "\u200B__BR__\u200B";

const normalizeMarkdown = (content: string): string => {
  return content
    .replace(/\\n/g, LINE_BREAK_MARKER) // Replace literal \n with line break marker
    .replace(/\n/g, LINE_BREAK_MARKER) // Replace actual newlines with line break marker
    .replace(/\\\*/g, "*") // Unescape \* to *
    .replace(/ {2,}/g, (match) => "\u00A0".repeat(match.length)) // Preserve multiple spaces
    .trim();
};

const renderMarkdown = (content: string): React.ReactNode => {
  const normalized = normalizeMarkdown(content);

  // Split by line break markers first, then process each segment
  const lines = normalized.split(LINE_BREAK_MARKER);
  const result: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      result.push(<br key={`br-${lineIndex}`} />);
    }

    // Matches: markdown links [text](url), plain URLs, bold **text**, italic *text*
    const combinedRegex = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s,)]+)|\*\*(.+?)\*\*|\*(.+?)\*/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = combinedRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        result.push(line.slice(lastIndex, match.index));
      }

      if (match[1] && match[2]) {
        // Markdown link: [text](url)
        result.push(
          <a
            key={`${lineIndex}-${match.index}`}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {match[1]}
          </a>,
        );
      } else if (match[3]) {
        // Plain URL: https://example.com
        result.push(
          <a
            key={`${lineIndex}-${match.index}`}
            href={match[3]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {match[3]}
          </a>,
        );
      } else if (match[4]) {
        // Bold: **text**
        result.push(<strong key={`${lineIndex}-${match.index}`}>{match[4]}</strong>);
      } else if (match[5]) {
        // Italic: *text*
        result.push(<em key={`${lineIndex}-${match.index}`}>{match[5]}</em>);
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      result.push(line.slice(lastIndex));
    }
  });

  return <span style={{ display: "inline" }}>{result}</span>;
};

export const SiteMessageBar: React.FC = () => {
  const styles = useSiteMessageBarStyles();
  const { values, dismissedIds, fetch, dismiss } = useAppStore((state) => state.siteMessages);

  useEffect(() => {
    fetch();
  }, []);

  const groupedMessages = useMemo(() => {
    const visible = values.filter((msg) => !dismissedIds.includes(msg.id));

    const groups = new Map<MessageSeverity, ISiteMessage[]>();
    visible.forEach((msg) => {
      const existing = groups.get(msg.severity) ?? [];
      existing.push(msg);
      groups.set(msg.severity, existing);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => severityOrder[a] - severityOrder[b]);
  }, [values, dismissedIds]);

  if (groupedMessages.length === 0) {
    return null;
  }

  const dismissGroup = (messages: ISiteMessage[]) => {
    messages.forEach((msg) => dismiss(msg.id));
  };

  return (
    <div
      className={styles.container}
      data-testid="siteMessageBarContainer"
      role="region"
      aria-label="Site announcements"
    >
      {groupedMessages.map(([severity, messages]) => (
        <div key={severity} className={styles.messageBarWrapper} data-testid="siteMessageBar">
          <MessageBar intent={mapSeverityToIntent(severity)}>
            <MessageBarBody>
              {messages.length === 1 ? (
                renderMarkdown(messages[0].markdown)
              ) : (
                <ul data-testid="siteMessageList">
                  {messages.map((msg) => (
                    <li key={msg.id}>{renderMarkdown(msg.markdown)}</li>
                  ))}
                </ul>
              )}
            </MessageBarBody>
            <MessageBarActions
              containerAction={
                <Button
                  appearance="transparent"
                  aria-label={`Dismiss ${severity} messages`}
                  icon={<DismissRegular />}
                  onClick={() => dismissGroup(messages)}
                  data-testid="dismissMessageBtn"
                />
              }
            />
          </MessageBar>
        </div>
      ))}
    </div>
  );
};
